import { Autocomplete, Box, Button, Card, CardContent, CardHeader, IconButton, TextField, Tooltip, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { UserCheck, UserPlus, Users, X } from "lucide-react"
import { useMemo, useState, type JSX } from "react"

import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { useToast } from "@/components/ui/use-toast"
import usePermissions from "@crm-src/hooks/usePermissions"
import { useUsers, type User } from "@crm-src/hooks/useUsers"
import { resellersApiService } from "@crm-src/lib/resellers-api"
import type { Reseller } from "@crm-src/types/lead"

type Salesperson = NonNullable<Reseller["assigned_salespeople"]>[number]

/** crm-api answers a refusal as `{ error }`; axios wraps it in `response.data`. */
function upstreamMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: unknown; errors?: unknown } } })?.response?.data
  if (typeof data?.error === "string") return data.error
  if (Array.isArray(data?.errors) && typeof data.errors[0] === "string") return data.errors[0]
  return fallback
}

/**
 * Assigned Salespeople on the reseller profile — and, for a holder of
 * `crm:resellers_salespeople`, the place to attach and detach OUR salespeople
 * (2026-09-15; its own grant since 2026-10-02).
 *
 * Before this the only way to change who looks after a reseller was the Edit
 * dialog's multi-select, which rewrote the whole list on save. Here each change
 * is ONE call — `assign_salesperson` / `remove_salesperson` — so adding Anna
 * cannot silently drop Ben because the dialog was opened on a stale list.
 *
 * WHO. `canAssignResellerSalespeople()` in the SPA — super admins, and anyone
 * an admin granted "Attach / remove salespeople on resellers" (per role or per
 * person). crm-api refuses everyone else with a 403 on its own (the strict
 * `crm_resellers_salespeople` claim) and holds an own-records holder to the
 * resellers it is attached to. Without the grant you see the list and no
 * controls — the same rule the Edit dialog follows.
 *
 * Removing someone goes through `<ConfirmDialog>`: the assignment is
 * deactivated (history survives), but the person loses the reseller from
 * their own-records scope at once, which is worth a second look.
 */
export function ResellerSalespeopleCard({
  reseller,
  onChanged,
}: {
  reseller: Reseller
  onChanged: () => void | Promise<void>
}): JSX.Element {
  const { canAssignResellerSalespeople } = usePermissions()
  const canManage = canAssignResellerSalespeople()
  const { toast } = useToast()
  const assigned = reseller.assigned_salespeople ?? []
  const [pendingRemove, setPendingRemove] = useState<Salesperson | null>(null)

  return (
    <Card data-cy="reseller-salespeople-card" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
            <Box sx={{ color: "text.secondary", display: "flex" }}><Users size={20} /></Box>
            <span>Assigned Salespeople</span>
            <Typography component="span" sx={{ fontSize: 13, color: "text.secondary", fontWeight: 400 }}>
              ({assigned.length})
            </Typography>
          </Box>
        }
      />
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {assigned.length > 0 ? (
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1 }} data-cy="reseller-salespeople-list">
            {assigned.map((person) => (
              <Box
                component="li"
                key={person.id}
                data-cy="reseller-salesperson"
                sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.25, bgcolor: "surfaceSoft", borderRadius: 1 }}
              >
                <Box
                  aria-hidden
                  sx={{
                    height: 32,
                    width: 32,
                    flexShrink: 0,
                    borderRadius: "50%",
                    bgcolor: (t) => alpha(t.palette.secondary.main, 0.14),
                    color: "secondary.dark",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <UserCheck size={16} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {person.full_name}
                  </Typography>
                  {person.email && (
                    <Typography sx={{ fontSize: 12, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {person.email}
                    </Typography>
                  )}
                </Box>
                {canManage && (
                  <Tooltip title="Remove from this reseller">
                    <IconButton
                      size="small"
                      aria-label={`Remove ${person.full_name}`}
                      data-cy="reseller-salesperson-remove"
                      onClick={() => setPendingRemove(person)}
                    >
                      <X size={16} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", color: "text.secondary", py: 3, bgcolor: "surfaceSoft", borderRadius: 1 }}>
            <Typography sx={{ fontSize: 14 }}>No salespeople assigned</Typography>
          </Box>
        )}

        {canManage && (
          <AddSalesperson
            resellerId={String(reseller.id)}
            assignedIds={assigned.map((p) => String(p.id))}
            onAdded={onChanged}
          />
        )}
      </CardContent>

      <ConfirmDialog
        isOpen={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        title="Remove salesperson"
        confirmLabel="Remove"
        description={
          pendingRemove
            ? `Remove ${pendingRemove.full_name} from ${reseller.name}? They stop seeing this reseller in their own records straight away.`
            : ""
        }
        onConfirm={async () => {
          if (!pendingRemove) return
          try {
            await resellersApiService.removeSalesperson(String(reseller.id), String(pendingRemove.id))
            toast({ title: `${pendingRemove.full_name} removed from ${reseller.name}` })
            await onChanged()
          } catch (error) {
            toast({
              variant: "destructive",
              title: "Couldn't remove the salesperson",
              description: upstreamMessage(error, "The CRM refused the change."),
            })
            throw error
          }
        }}
      />
    </Card>
  )
}

/** The picker. Its own component so the user list is only fetched for someone
 * who can use it. `/users` is an admin read, so a non-admin assigner gets the
 * assignable roster (names only) instead. */
function AddSalesperson({
  resellerId,
  assignedIds,
  onAdded,
}: {
  resellerId: string
  assignedIds: string[]
  onAdded: () => void | Promise<void>
}): JSX.Element {
  const { users, loading } = useUsers({ assignableFallback: true })
  const { toast } = useToast()
  const [picked, setPicked] = useState<User | null>(null)
  const [saving, setSaving] = useState(false)

  const options = useMemo(
    () => users.filter((u) => !assignedIds.includes(String(u.id))),
    [users, assignedIds],
  )

  const add = async (): Promise<void> => {
    if (!picked) return
    setSaving(true)
    try {
      await resellersApiService.assignSalesperson(resellerId, String(picked.id))
      toast({ title: `${picked.full_name} assigned` })
      setPicked(null)
      await onAdded()
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Couldn't assign the salesperson",
        description: upstreamMessage(error, "The CRM refused the change."),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ mt: "auto", display: "flex", gap: 1, alignItems: "flex-start" }}>
      <Autocomplete
        size="small"
        fullWidth
        options={options}
        value={picked}
        loading={loading}
        onChange={(_e, value) => setPicked(value)}
        getOptionLabel={(u) => u.full_name}
        isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
        noOptionsText={loading ? "Loading…" : "Everyone is already assigned"}
        renderOption={(props, u) => (
          <Box component="li" {...props} key={u.id}>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Typography sx={{ fontSize: 14 }}>{u.full_name}</Typography>
              {u.email && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{u.email}</Typography>}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Add a salesperson…"
            inputProps={{ ...params.inputProps, "data-cy": "reseller-salesperson-picker" }}
          />
        )}
      />
      <Button
        variant="contained"
        size="small"
        startIcon={<UserPlus size={16} />}
        disabled={!picked || saving}
        onClick={() => void add()}
        data-cy="reseller-salesperson-add"
        sx={{ flexShrink: 0, height: 40 }}
      >
        Add
      </Button>
    </Box>
  )
}
